import angular from 'angular';
import csmCommonWeb from 'csm-common-web';
import OpenDocumentsDialog from '../../dialogs/OpenDocumentsDialog/OpenDocumentsDialog';
import booksService from '../../util/BooksSvc/BooksSvc';
import entityMaker from '../../util/entityMaker/entityMaker';
import booksSettings from '../../util/BooksSettings/BooksSettings';

import invoiceVendorTemplate from './invoiceVendor.template.html';
import './invoiceVendor.css';

export default angular
  .module('books.ui.invoiceVendor', [csmCommonWeb, OpenDocumentsDialog, booksService, entityMaker, booksSettings])
  .directive('invoiceVendor', invoiceVendorDirective)
  .name;

invoiceVendorDirective.$inject = []
  
/*****************************************************************************
 * Directive: invoiceVendor
 *
 * @ngdoc    directive
 * @name     invoiceVendor
 * @memberof books.ui.invoiceVendor
 * @restrict E
 *
 * @description
 * Directive to display the vendor portion of the invoice input fields. Template
 * includes a vendor selector with the option of adding new vendors; a memo,
 * invoice number and date fields. To be used with the invoiceItems directive.
 *
 * @attr {expression} [hideOpenDocs] Property to determine if the open documents
 *                    button should be hidden or displayed automatically
 * @attr {expression} [includeInvoices] Property to determine if invoices should
 *                    be included in the open documents search results
 * @attr {object}     [invoice] Object to hold the vendor properties for the invoice.
 * @attr {expression} [lockVendor] Flag indicating if the vendor input filed
 *                    should be locked
 * @attr {function}   [restrictVendors] Property to determine if the user should
 *                    to be restricted from creating new vendors.
 * @attr {expression} [reviewOnly] Flag indicating that the current invoice can not
 *                    modified.
 * @attr {expression} [vendorsOnly] Flag indicating only vendors should be returned
 *                    in the PO search
 * @attr {object}     [vendors] Dataprovider for the vendors typeahead.
 *****************************************************************************/
/* @ngInject */ function invoiceVendorDirective() {
  /*---------------------------------*/
  /* Add style for directive element */
  /*---------------------------------*/
  var mBody = angular.element(document.querySelector('body'));
  mBody.append('<style>invoice-vendor { display: block} </style>');

  invoiceVendorController.$inject = [
    '$element',
    '$scope',
    '$q',
    '$uibModal',
    'Alert',
    'BooksSettings',
    'SERVER',
    'BooksSvc',
    'BusyScreen',
    'EntityMaker',
    'OpenDocumentsDialog'
  ];

  /* @ngInject */
  function invoiceVendorController(
    $element,
    $scope,
    $q,
    $uibModal,
    Alert,
    BooksSettings,
    SERVER,
    BooksSvc,
    BusyScreen,
    EntityMaker,
    OpenDocumentsDialog
  ) {
    /** Current vendor object. */ var mSavedVendor;

    /*-------*/
    /* Flags */
    /*-------*/
    var mEntityMakerActive = false;
    var mIsNew;
    var mPOPanelActive = false;

    /* Entity search based on vendor-only flag passed to directive */
    var searchType = $scope.vendorsOnly
      ? SERVER.BooksDBConstants.ET_VENDOR.toString()
      : BooksSettings.ledgerEntityTypes();

    /***************************************************************************
     * getVendors */
    /**
     * Returns a list of entities that match the given text. Entity search based
     * on vendorsOnly flag.
     *
     * @param   text   Current input text
     * @return         Array of sorted corresponding matching entities
     ***************************************************************************/
    $scope.getVendors = function (text) {
      /*---------------------------------*/
      /* No input passed, return nothing */
      /*---------------------------------*/
      if (!text) return $q.resolve();

      var defer = $q.defer();
      BooksSvc.findAddressEntities(text, searchType, true).then(
        function (resp) {
          var entities = resp.data.sort(function (a, b) {
            return a.entity.fullName
              .toLowerCase()
              .localeCompare(b.entity.fullName.toLowerCase());
          });
          defer.resolve(entities);
        }
      );
      return defer.promise;
    };

    /*****************************************************************************
     * handlePurchaseOrders */
    /**
     * Displays the Open Documents modal dialog. If a vendor has been provided,
     * the documents for the vendor are automatically searched and displayed.
     *****************************************************************************/
    $scope.handlePurchaseOrders = function () {
      if (mPOPanelActive) return;
      else mPOPanelActive = true;

      var entity = typeof $scope.vendor == 'object' ? $scope.vendor : null;

      OpenDocumentsDialog.show(
        entity,
        false,
        $scope.includeInvoices,
        $scope.vendorsOnly
      )
        .then(function (poItems) {
          if (!$scope.invoice.items) $scope.invoice.items = [];

          /*--------------------------------*/
          /* Convert PO items to bill items */
          /*--------------------------------*/
          var billItems =
            OpenDocumentsDialog.convertPOItemsToBillItems(poItems);
          angular.forEach(billItems, function (bi) {
            /*----------------------------------------------------------*/
            /* Check against the appropriate list of items to make sure */
            /* an existing item for the purchase order doesn't exist.   */
            /*----------------------------------------------------------*/
            var matches = $scope.invoice.items.filter(function (item) {
              return item.poItemID == bi.poItemID;
            });
            var matchedItem = matches.length > 0 ? matches[0] : null;

            /*-----------------------------------------*/
            /* No matching item found, add to the list */
            /*-----------------------------------------*/
            if (!matchedItem) $scope.invoice.items.push(bi);
            /*--------------------------------------*/
            /* Otherwise update the matched item to */
            /* the full amount of the created item. */
            /*--------------------------------------*/ else {
              matchedItem.quantity = bi.quantity;
              matchedItem.total = bi.total;
            }
          });

          /* set memo if it's empty and a single PO was added to invoice */
          if (!$scope.invoice.memo && poItems.length == 1)
            $scope.invoice.memo = poItems[0].number + ': ' + poItems[0].name;

          /*----------------------------------------------------*/
          /* Make a new array reference to trigger watcher in   */
          /* invoiceItems directive so the grid will be updated */
          /*----------------------------------------------------*/
          $scope.invoice.items = $scope.invoice.items.concat();
        })
        .finally(function () {
          mPOPanelActive = false;
        });
    };

    /*****************************************************************************
     * isNew */
    /**
     * New Invoice Indicator
     *****************************************************************************/
    $scope.isNew = function () {
      return mIsNew;
    };

    /***************************************************************************
     * onFor */
    /**
     * Fires when the 'For' field receives focus or loses focus. If the
     * model is not an object and is not empty, then the user has not selected
     * an existing vendor. Display the entity maker with the user's entry.
     ***************************************************************************/
    $scope.onFor = function ($event) {
      /*--------------------------------------------*/
      /* Save the current vendor reference on focus */
      /*--------------------------------------------*/
      if ($event.type == 'focus') {
        mSavedVendor = $scope.vendor;
        return;
      }

      /*----------------------------------------------------------------*/
      /* The focus is still on the field, do nothing. This will         */
      /* usually be from the selection of an vendor with the type ahead */
      /*----------------------------------------------------------------*/
      if (
        document.activeElement ==
        document.getElementsByClassName('vendor-select')[0]
      )
        return;

      /*----------------------------------------------*/
      /* No vendor bound data, clear invoice property */
      /*----------------------------------------------*/
      if (!$scope.vendor) {
        $scope.invoice.vendorID = NaN;
        return;
      }

      /*----------------------------------------------*/
      /* If the model is an object, there has been an */
      /* vendor selection and needs to be processed   */
      /*----------------------------------------------*/
      if (typeof $scope.vendor == 'object') {
        selectVendor($scope.vendor);
        /*--------------------------------------------*/
        /* If Open Documents button is hidden, do not */
        /* display the Open Documents dialog on blur  */
        /*--------------------------------------------*/
        if (!$scope.hideOpenDocs) $scope.handlePurchaseOrders();
      } else if ($scope.restrictVendors()) {
        /*--------------------------------*/
        /* Display a message if they are  */
        /* not allowed to create vendors. */
        /*--------------------------------*/
        $event.target.value = '';
        return Alert.info('You must select an existing vendor.', 'Vendors');
      } else if (!$scope.restrictVendors()) {
        /*-------------------------------------------------------------*/
        /* Otherwise, the model is text corresponding to the unmatched */
        /* for input. Use the Entity Maker to process the input        */
        /*-------------------------------------------------------------*/
        mEntityMakerActive = true;
        EntityMaker.newEntity(
          $scope.vendor,
          SERVER.BooksDBConstants.ET_VENDOR
        )
          .then(
            function (result) {
              BusyScreen.setBusy(true);

              /*-----------------------------------------------------------------*/
              /* Create AddressEntityListItem from the new vendor and select it. */
              /*-----------------------------------------------------------------*/
              var listItem = {
                entity: result.entity,
                address: result.entity.addressInfo
              };
              selectVendor(listItem);

              BusyScreen.setBusy(false);
            },
            function () {
              $event.target.focus();
            }
          )
          .finally(function () {
            mEntityMakerActive = false;
          });
      }
    };

    /*****************************************************************************
     * selectVendor */
    /**
     * Set the vendor on the scope and update the invoice model.
     *
     * @param   vendor     Vendor entity object
     *****************************************************************************/
    function selectVendor(vendor) {
      $scope.vendor = vendor;

      /*-------------------------------------*/
      /* Update the saved vendor and save id */
      /*-------------------------------------*/
      if (vendor != mSavedVendor) {
        if ($scope.vendor) $scope.invoice.vendorID = $scope.vendor.entity.id;
      }
    }

    /***************************************************************************
     * vendorLabel */
    /**
     * Label function for the invoice vendor typeahead selection.
     *
     * @param   v      Vendor entity object
     * @return         Formatted string in form "Vendor Name (Vendor Address)"
     ***************************************************************************/
    $scope.vendorLabel = function (v) {
      if (angular.isObject(v))
        return (
          (v.entity && v.entity.name ? v.entity.name : '') +
          ' ' +
          (v.address &&
          v.address.address &&
          v.address.city &&
          v.address.state &&
          v.address.zip
            ? '(' +
              v.address.address +
              ', ' +
              v.address.city +
              ', ' +
              v.address.state +
              ' ' +
              v.address.zip +
              ')'
            : '')
        );
      else return '';
    };

    /*-------------------------------------------------------*/
    /* Set the corresponding vendor when the invoice changes */
    /*-------------------------------------------------------*/
    $scope.$watch('invoice', function (invoice) {
      if (invoice && invoice.vendorID) selectVendor(invoice.vendor);
      else if (invoice && !invoice.vendorID) selectVendor(null);

      mIsNew = invoice && !invoice.id;
    });

    /*------------------------------------------------------------------------------------*/
    /* Changes to angular (possibly the addition of debounce to uibTypeahead) is causing  */
    /* issues when setting focus on a disabled input. Specifically, in lazyTypeahead, the */
    /* blur is firing before the typeahead has a chance to complete initialization. This  */
    /* then calls the blur function of the lazyTypeahead, and will clear out the ngModel. */
    /*                                                                                    */
    /* In order to get around the issue for now, we are utilizing the csm-focus attribute */
    /* which allows us to pass a condition for whether to set the focus. In this case, we */
    /* are using the opposite condition compared to the ng-disabled.                      */
    /*                                                                                    */
    /* TODO This will need to be looked at further when we better understand what the     */
    /* changes to angular have affected.                                                  */
    /*------------------------------------------------------------------------------------*/
    $scope.shouldFocus = !$scope.reviewOnly && !$scope.lockVendor;
  };

  return {
    restrict: 'E',
    controller: invoiceVendorController,
    template: invoiceVendorTemplate,
    scope: {
      invoice: '=' /** Invoice object    */,
      vendor: '=' /** Selected vendor  */,

      /** Flags */
      lockVendor: '=' /** Lock vendor input field if true */,
      includeInvoices:
        '=' /** Include invoices in open documents search results, if true. Passed to OpenDocumentsDialog  */,
      hideOpenDocs: '=' /** Hide the Open Documents button if true */,
      restrictVendors: '&' /** Disable entityMaker if true */,
      reviewOnly:
        '=' /** Prevent modifications to the current invoice if true. */,
      vendorsOnly:
        '=' /** Return only vendors entities in open document search if true. Passed to the OpenDocumentsDialog  */
    }
  };
}
